/**
 * Platform-admin (super-admin console) React Query hooks.
 *
 * Distinct from `useMe()` / `useHasPermission()` — the platform console
 * crosses tenant boundaries and the per-org JWT claim isn't consulted.
 * `usePlatformMe()` is the entry point: it polls `/v1/platform/me`
 * which is `AllowAny`, so unauthenticated callers get a `false` answer
 * without first knowing whether they qualify.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, ApiPaths } from "./api";
import { tokenStore } from "./tokenStore";
import type {
  PlatformAuditEventRow,
  PlatformKpis,
  PlatformMe,
  PlatformOrgDetail,
  PlatformOrgListResponse,
  PlatformPartnersResponse,
} from "./types";

const PLATFORM_ME_KEY = ["platform-me"] as const;
const PLATFORM_KPIS_KEY = ["platform-kpis"] as const;
const PLATFORM_ORG_LIST_KEY = (params: PlatformOrgsParams) =>
  ["platform-orgs", params] as const;
const PLATFORM_ORG_DETAIL_KEY = (id: number | string) =>
  ["platform-org", String(id)] as const;
const PLATFORM_AUDIT_EVENTS_KEY = (params: PlatformAuditEventsParams) =>
  ["platform-audit-events", params] as const;

const PLATFORM_PARTNERS_KEY = ["platform-partners"] as const;

/* --------------- partners --------------- */

export function usePlatformPartners() {
  return useQuery<PlatformPartnersResponse>({
    queryKey: PLATFORM_PARTNERS_KEY,
    queryFn: async () =>
      (await api.get<PlatformPartnersResponse>(ApiPaths.platformPartners)).data,
    enabled: !!tokenStore.getAccess(),
  });
}

export type UpdatePartnerTermsInput = {
  kind: "reseller" | "commercial";
  id: string;
  commission_model?: "percentage" | "fixed";
  rate_base_pct?: string;
  rate_session_pct?: string;
  rate_signature_pct?: string;
  fixed_base_eur?: string;
  fixed_session_eur?: string;
  fixed_signature_eur?: string;
  commission_duration?: string;
  status?: string;
};

export function useUpdatePartnerTerms() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ kind, id, ...body }: UpdatePartnerTermsInput) =>
      (await api.patch(ApiPaths.platformPartnerUpdate(kind, id), body)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PLATFORM_PARTNERS_KEY });
    },
  });
}

/** Super-admin registers a NexBasira-direct commercial (no reseller). */
export type CreatePlatformCommercialInput = {
  name: string;
  email: string;
  phone?: string;
  is_company?: boolean;
  legal_name?: string;
  registration_number?: string;
  vat_id?: string;
  address_line?: string;
  city?: string;
  postal_code?: string;
  address_country?: string;
  commission_model?: "percentage" | "fixed";
  rate_base_pct?: string;
  rate_session_pct?: string;
  rate_signature_pct?: string;
  fixed_base_eur?: string;
  fixed_session_eur?: string;
  fixed_signature_eur?: string;
  commission_duration?: string;
};

export function useCreatePlatformCommercial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreatePlatformCommercialInput) =>
      (
        await api.post<{
          id: string;
          referral_code: string;
          invited: boolean;
        }>(ApiPaths.platformCommercials, input)
      ).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PLATFORM_PARTNERS_KEY });
    },
  });
}

/** Resend a direct commercial's account-opening email. */
export function useResendPlatformCommercialInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (
        await api.post<{ status: string; email: string; expires_at?: string }>(
          ApiPaths.platformCommercialInvite(id),
        )
      ).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PLATFORM_PARTNERS_KEY });
    },
  });
}

/** Resend an org's admin account-opening email (platform side). */
export function useResendPlatformOrgInvite() {
  return useMutation({
    mutationFn: async (orgId: number | string) =>
      (
        await api.post<{ status: string; email: string }>(
          ApiPaths.platformOrgResendInvite(orgId),
        )
      ).data,
  });
}

/* --------------- /me --------------- */

export function usePlatformMe() {
  return useQuery<PlatformMe>({
    queryKey: PLATFORM_ME_KEY,
    queryFn: async () => {
      const res = await api.get<PlatformMe>(ApiPaths.platformMe);
      return res.data;
    },
    // `/me` is AllowAny — keep polling enabled even pre-login so the
    // console can detect a freshly-logged-in staff user without a
    // page reload. The query is cheap (200 bytes, no DB write).
    enabled: !!tokenStore.getAccess(),
    staleTime: 60_000,
  });
}

/* --------------- KPIs --------------- */

export function usePlatformKpis() {
  return useQuery<PlatformKpis>({
    queryKey: PLATFORM_KPIS_KEY,
    queryFn: async () => {
      const res = await api.get<PlatformKpis>(ApiPaths.platformKpis);
      return res.data;
    },
    staleTime: 30_000,
  });
}

/* --------------- Org list --------------- */

export type PlatformOrgsParams = {
  status?: "active" | "suspended";
  kyc?: "unverified" | "pending" | "verified" | "rejected";
  flagged?: boolean;
  sort?: "-created_at" | "created_at" | "kyc_status" | "name";
  limit?: number;
  cursor?: string;
};

export function usePlatformOrgs(params: PlatformOrgsParams = {}) {
  return useQuery<PlatformOrgListResponse>({
    queryKey: PLATFORM_ORG_LIST_KEY(params),
    queryFn: async () => {
      const search = new URLSearchParams();
      if (params.status) search.set("status", params.status);
      if (params.kyc) search.set("kyc", params.kyc);
      if (params.flagged) search.set("flagged", "1");
      if (params.sort) search.set("sort", params.sort);
      if (params.limit) search.set("limit", String(params.limit));
      if (params.cursor) search.set("cursor", params.cursor);
      const qs = search.toString();
      const url = qs ? `${ApiPaths.platformOrgs}?${qs}` : ApiPaths.platformOrgs;
      const res = await api.get<PlatformOrgListResponse>(url);
      return res.data;
    },
  });
}

/* --------------- Org detail --------------- */

export function usePlatformOrgDetail(id: number | string | null | undefined) {
  return useQuery<PlatformOrgDetail>({
    queryKey: PLATFORM_ORG_DETAIL_KEY(id ?? ""),
    queryFn: async () => {
      const res = await api.get<PlatformOrgDetail>(
        ApiPaths.platformOrgDetail(id!),
      );
      return res.data;
    },
    enabled: id !== null && id !== undefined && id !== "",
  });
}

/* --------------- Mutations --------------- */

type KycInput = { kyc_status: string; reason?: string };

export function usePlatformSetKycStatus(orgId: number | string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: KycInput) => {
      const res = await api.patch<PlatformOrgDetail>(
        ApiPaths.platformOrgKycStatus(orgId),
        input,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PLATFORM_ORG_DETAIL_KEY(orgId) });
      void qc.invalidateQueries({ queryKey: ["platform-orgs"] });
      void qc.invalidateQueries({ queryKey: PLATFORM_KPIS_KEY });
      void qc.invalidateQueries({ queryKey: ["platform-audit-events"] });
    },
  });
}

type AnchorPolicyInput = {
  blockchain_immediate?: boolean;
  eidas_scope?: "final_only" | "everything";
  reason?: string;
};

export function usePlatformSetAnchorPolicy(orgId: number | string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AnchorPolicyInput) => {
      const res = await api.patch<PlatformOrgDetail>(
        ApiPaths.platformOrgAnchorPolicy(orgId),
        input,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PLATFORM_ORG_DETAIL_KEY(orgId) });
      void qc.invalidateQueries({ queryKey: ["platform-audit-events"] });
    },
  });
}

type ActionInput = { reason?: string };

export function usePlatformSuspendOrg(orgId: number | string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ActionInput) => {
      const res = await api.post(
        ApiPaths.platformOrgSuspend(orgId),
        input,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PLATFORM_ORG_DETAIL_KEY(orgId) });
      void qc.invalidateQueries({ queryKey: ["platform-orgs"] });
      void qc.invalidateQueries({ queryKey: ["platform-audit-events"] });
    },
  });
}

type ForceAnchorResult = {
  session_id: string;
  org_id: number;
  tokens: Array<{ id: string; backend: string; state: string }>;
};

type SanctionsOverrideInput = {
  until: string | null;
  reason?: string;
};

export function usePlatformSanctionsOverride(orgId: number | string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SanctionsOverrideInput) => {
      const res = await api.post<PlatformOrgDetail>(
        ApiPaths.platformOrgSanctionsOverride(orgId),
        input,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PLATFORM_ORG_DETAIL_KEY(orgId) });
      void qc.invalidateQueries({ queryKey: ["platform-orgs"] });
      void qc.invalidateQueries({ queryKey: ["platform-audit-events"] });
    },
  });
}

type BulkAnchorInput = {
  since: string;
  until: string;
  org_id?: number;
  limit?: number;
};

type BulkAnchorResult = {
  since: string | null;
  until: string | null;
  org_id: number | null;
  sessions_attempted: number;
  sessions_succeeded: number;
  sessions_failed: number;
  tokens_touched: number;
  truncated_at_limit: boolean;
};

export function usePlatformBulkAnchor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: BulkAnchorInput): Promise<BulkAnchorResult> => {
      const res = await api.post<BulkAnchorResult>(
        ApiPaths.platformSessionBulkAnchor,
        input,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["platform-audit-events"] });
    },
  });
}

type StampNowResult = {
  stamped: number;
  tokens: Array<{
    id: string;
    backend: string;
    state: string;
    covers_sequence_through: number;
  }>;
};

export function usePlatformStampNow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<StampNowResult> => {
      const res = await api.post<StampNowResult>(
        ApiPaths.platformStampNow,
        {},
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["platform-audit-verify"] });
    },
  });
}

export function usePlatformForceAnchor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sessionId: string): Promise<ForceAnchorResult> => {
      const res = await api.post<ForceAnchorResult>(
        ApiPaths.platformSessionForceAnchor(sessionId),
        {},
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["platform-audit-events"] });
    },
  });
}

export function usePlatformReinstateOrg(orgId: number | string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ActionInput) => {
      const res = await api.post(
        ApiPaths.platformOrgReinstate(orgId),
        input,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: PLATFORM_ORG_DETAIL_KEY(orgId) });
      void qc.invalidateQueries({ queryKey: ["platform-orgs"] });
      void qc.invalidateQueries({ queryKey: ["platform-audit-events"] });
    },
  });
}

/* --------------- Audit events --------------- */

export type PlatformAuditEventsParams = {
  org?: number | string;
  kind?: string;
  actor?: number | string;
  limit?: number;
};

export type PlatformChainVerifyResult = {
  ok: boolean;
  total: number;
  broken: {
    sequence: number;
    expected_hash: string;
    stored_hash: string;
    expected_prev_hash: string;
    stored_prev_hash: string;
  } | null;
  chain_head_hash: string;
};

export function usePlatformChainVerify() {
  return useQuery<PlatformChainVerifyResult>({
    queryKey: ["platform-audit-verify"],
    queryFn: async () => {
      const res = await api.get<PlatformChainVerifyResult>(
        ApiPaths.platformAuditEventsVerify,
      );
      return res.data;
    },
    staleTime: 30_000,
  });
}

export function usePlatformAuditEvents(params: PlatformAuditEventsParams = {}) {
  return useQuery<{ results: PlatformAuditEventRow[] }>({
    queryKey: PLATFORM_AUDIT_EVENTS_KEY(params),
    queryFn: async () => {
      const search = new URLSearchParams();
      if (params.org) search.set("org", String(params.org));
      if (params.kind) search.set("kind", params.kind);
      if (params.actor) search.set("actor", String(params.actor));
      if (params.limit) search.set("limit", String(params.limit));
      const qs = search.toString();
      const url = qs
        ? `${ApiPaths.platformAuditEvents}?${qs}`
        : ApiPaths.platformAuditEvents;
      const res = await api.get<{ results: PlatformAuditEventRow[] }>(url);
      return res.data;
    },
  });
}

/** Cross-tenant invoice list + reconciliation ops (P5, platform staff). */
export function usePlatformInvoices(filter: string) {
  return useQuery<
    (import("./types").BillingInvoice & { bill_to?: string; billing_model?: string })[]
  >({
    queryKey: ["platform-invoices", filter],
    queryFn: async () =>
      (
        await api.get(
          ApiPaths.platformInvoices + (filter ? `?${filter}` : ""),
        )
      ).data,
    enabled: !!tokenStore.getAccess(),
  });
}

export function usePlatformInvoiceAction(kind: "mark-paid" | "void") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      (
        await api.post(
          kind === "mark-paid"
            ? ApiPaths.platformInvoiceMarkPaid(id)
            : ApiPaths.platformInvoiceVoid(id),
        )
      ).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["platform-invoices"] });
    },
  });
}
